import { type JSX,useCallback, useEffect, useState } from 'react';

import { Box, Stack, TextField } from '@mui/material';
import { useAppDispatch,useAppSelector } from 'app/hooks';
import { useDrawerContext } from 'common/components/DrawerContext/drawerContext';
import { EditDrawer } from 'common/components/EditDrawer';
import {
    selectCurrentEntity,
    setCurrentEntity,
    setSaveButtonEnabled
} from 'common/components/EditDrawer/editDrawerSlice';
import { DEFAULT_ERROR_HANDLER_INLINE_STYLES } from 'common/components/errorHandler/ErrorHandler';
import { getOptionLabelDefaultCallback,isOptionEqualToValueDefaultCallback } from 'common/components/sharedCustomComponents/Common/helpers';
import { EntitiesSearchableCommonDropDown } from 'common/components/sharedCustomComponents/EntitiesSearchableCommonDropDown/EntitiesSearchableCommonDropDown';
import { fetchForeignSystems } from 'common/services/api/ForeignSystems/ForeignSystems.thunk';
import {
    APIUserProfileForeignSystemsPostParams
} from 'common/services/api/UserProfileForeignSystems/UserProfileForeignSystems.api';
import {
    PostUserForeignSystemThunkResponse,
    postUserProfileForeignSystem,
    PutUserForeignSystemThunkResponse,
    putUserProfileForeignSystem } from 'common/services/api/UserProfileForeignSystems/UserProfileForeignSystems.thunk';
import { ForeignSystem,UserForeignKey } from 'common/types/Entities';
import { DrawerEntitiesNames, EditDrawerContentProps,Operations } from 'common/types/TypeDefs';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { selectUserDetails } from 'features/users/usersSlice';

import { setUserProfileForeignKeys } from '../userProfileForeignSystemsSlice';

const renderForeignSystemName = (option: ForeignSystem) => <>{option.name}</>;

interface UserForeignSystemDrawerProperties {
    userId: string;
}

function useUserForeignSystemDrawer(
    entityName: string,
    drawerProps: UserForeignSystemDrawerProperties,
    editingItemInfo?: EditDrawerContentProps<UserForeignKey>['editingItemInfo']
) {
    const dispatch = useAppDispatch();
    const [foreignSystem, setForeignSystem] = useState<ForeignSystem | null>(null);
    const [foreignKey, setForeignKey] = useState<string>('');
    const [userForeignKeyToEdit, setUserForeignKeyToEdit] = useState<UserForeignKey | null>(null);

    const drawerContext = useDrawerContext<
        UserForeignKey,
        UserForeignSystemDrawerProperties,
        APIUserProfileForeignSystemsPostParams,
        PostUserForeignSystemThunkResponse,
        APIUserProfileForeignSystemsPostParams,
        PutUserForeignSystemThunkResponse
    >(entityName, drawerProps, editingItemInfo);

    useEffect(() => {
        if (drawerContext.currentId === DrawerIDs.UserProfileForeignSystems) {
            switch (drawerContext.action) {
                case Operations.Link: {
                    setForeignSystem(null);
                    setForeignKey('');
                    setUserForeignKeyToEdit(null);
                    break;
                }
                case Operations.Edit: {
                    setUserForeignKeyToEdit(editingItemInfo?.editingRow ?? null);
                    setForeignKey(editingItemInfo?.editingRow?.foreignKey ?? '');
                    break;
                }
            }
        }
    }, [drawerContext.isOpenDrawer, drawerContext.currentId, drawerContext.action, editingItemInfo?.editingRow]);

    const handleForeignSystemChange = useCallback((_e: unknown, userForeignSystem: ForeignSystem | null) => {
        setForeignSystem(userForeignSystem);
        dispatch(setSaveButtonEnabled(!!userForeignSystem?.id && !!foreignKey));
    }, [dispatch, foreignKey]);

    const handleForeignKeyChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(event => {
        setForeignKey(event.target.value);
        dispatch(setSaveButtonEnabled(
            (!!foreignSystem?.id || !!userForeignKeyToEdit?.foreignSystemId)
             && !!event.target.value
        ));
    }, [dispatch, foreignSystem?.id, userForeignKeyToEdit?.foreignSystemId]);

    const editSuccessHandler = useCallback((putResult: PutUserForeignSystemThunkResponse) => {
        drawerContext.editSuccessHandler(putResult);
        dispatch(setUserProfileForeignKeys(putResult.rows));
        dispatch(setCurrentEntity());
    }, [drawerContext, dispatch]);

    const createSuccessHandler = useCallback((postResult: PostUserForeignSystemThunkResponse) => {
        drawerContext.createSuccessHandler(postResult);
        dispatch(setUserProfileForeignKeys(postResult.rows));
        dispatch(setCurrentEntity());
    }, [drawerContext, dispatch]);

    const getPostOptions = useCallback((): APIUserProfileForeignSystemsPostParams => ({
        userId: drawerProps.userId,
        foreignSystemId: foreignSystem?.id ?? '',
        foreignKey: foreignKey
    }), [drawerProps.userId, foreignSystem?.id, foreignKey]);

    const getPutOptions = useCallback((): APIUserProfileForeignSystemsPostParams => ({
        userId: drawerProps.userId,
        foreignSystemId: userForeignKeyToEdit?.foreignSystemId ?? '',
        foreignKey: foreignKey
    }), [drawerProps.userId, userForeignKeyToEdit?.foreignSystemId, foreignKey]);

    const postCallback = useCallback(() => {
        drawerContext.postCallback(postUserProfileForeignSystem, getPostOptions, createSuccessHandler);
    }, [drawerContext, getPostOptions, createSuccessHandler]);

    const putCallback = useCallback(() => {
        drawerContext.putCallback(putUserProfileForeignSystem, getPutOptions, editSuccessHandler);
    }, [drawerContext, getPutOptions, editSuccessHandler]);

    return {
        ...drawerContext,
        foreignSystem,
        foreignKey,
        handleForeignSystemChange,
        handleForeignKeyChange,
        postCallback,
        putCallback
    };
}

function CreateContent({
    entityName,
    processing,
    nameInConflict,
    foreignSystem,
    foreignKey,
    handleForeignSystemChange,
    handleForeignKeyChange,
    postCallback
}: Readonly<{
    entityName: string;
    processing: boolean;
    nameInConflict: boolean;
    foreignSystem: ForeignSystem | null;
    foreignKey: string;
    handleForeignSystemChange: (_e: unknown, userForeignSystem: ForeignSystem | null) => void;
    handleForeignKeyChange: React.ChangeEventHandler<HTMLInputElement>;
    postCallback: () => void;
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={postCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <EntitiesSearchableCommonDropDown
                    initialEntityOption={{
                        id: '',
                        name: ''
                    }}
                    value={foreignSystem}
                    onChange={handleForeignSystemChange}
                    label={entityName}
                    getOptionLabel={getOptionLabelDefaultCallback}
                    isOptionEqualToValue={isOptionEqualToValueDefaultCallback}
                    asyncThunkAction={fetchForeignSystems}
                    asyncThunkArgs={undefined}
                    displayMenuItemContent={renderForeignSystemName}
                    hasError={nameInConflict}
                />
                {nameInConflict &&
                    <Box component='div'
                        display='inline'
                        sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}
                    >
                        This User already has a value for this Foreign System.
                    </Box>
                }
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label="Foreign System Key"
                    variant="filled"
                    value={foreignKey}
                    onChange={handleForeignKeyChange}
                />
            </Stack>
        </EditDrawer>
    );
}

function EditContent({
    entityName,
    processing,
    foreignKey,
    handleForeignKeyChange,
    putCallback,
    editingItemInfo
}: Readonly<{
    entityName: string;
    processing: boolean;
    foreignKey: string;
    handleForeignKeyChange: React.ChangeEventHandler<HTMLInputElement>;
    putCallback: () => void;
    editingItemInfo?: EditDrawerContentProps<UserForeignKey>['editingItemInfo'];
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={putCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label={entityName}
                    variant="filled"
                    defaultValue={editingItemInfo?.editingRow?.foreignSystemName ?? ''}
                    disabled
                />
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label="Foreign System Key"
                    variant="filled"
                    value={foreignKey}
                    onChange={handleForeignKeyChange}
                />
            </Stack>
        </EditDrawer>
    );
}

export function UserForeignSystemDrawer(props: EditDrawerContentProps<UserForeignKey>['editingItemInfo']) {
    const userDetails = useAppSelector(selectUserDetails);
    const currentEntity = useAppSelector(selectCurrentEntity);
    const roleDrawerProps = {
        userId: userDetails?.userId ?? ''
    };

    const drawerState = useUserForeignSystemDrawer('Foreign System', roleDrawerProps, props);

    if (currentEntity !== DrawerEntitiesNames.UserForeignSystems) {
        return null;
    }

    if (drawerState.action === Operations.Create || drawerState.action === Operations.Link) {
        return (
            <CreateContent
                entityName="Foreign System"
                processing={drawerState.processing}
                nameInConflict={drawerState.nameInConflict}
                foreignSystem={drawerState.foreignSystem}
                foreignKey={drawerState.foreignKey}
                handleForeignSystemChange={drawerState.handleForeignSystemChange}
                handleForeignKeyChange={drawerState.handleForeignKeyChange}
                postCallback={drawerState.postCallback}
            />
        );
    }

    if (drawerState.action === Operations.Edit) {
        if (!props) {
            return <></>;
        }
        return (
            <EditContent
                entityName="Foreign System"
                processing={drawerState.processing}
                foreignKey={drawerState.foreignKey}
                handleForeignKeyChange={drawerState.handleForeignKeyChange}
                putCallback={drawerState.putCallback}
                editingItemInfo={props}
            />
        );
    }

    return <></>;
}
